import axios from 'axios'
import _ from 'lodash'
import moment, { Moment } from 'moment'
import Cookies from 'universal-cookie'
import { NewHabitType } from './components/TargetScores'
import { HighFive, Note, Player, ProfileData } from './types'
import { loginCookieId } from './utils/constants'
import { MiwProgress, Score, Target } from './utils/dataFormatting'
import { Headline } from './utils/headlines'

const apiUrl = process.env.REACT_APP_API_URL

function throwIfNotSuccessful(response: any) {
  if (response.wasSuccessful === false) {
    throw new Error(response.message)
  } else {
    return response
  }
}

async function post(url: string, params: any) {
  return (await axios.post(url, params)).data
}

export function getSessionKey() {
  const cookies = new Cookies()
  return _.get(cookies.get(loginCookieId), 'sessionKey')
}

export interface DashboardData {
  player: Player
  targets: Target[]
  scores: Score[]
  notes: Note[]
  highFives: HighFive[]
  teammate: Player
  teammateScores: Score[]
}

export interface AppData {
  dashboardData: DashboardData
  roundNumber: number
  roundStartDay: Moment
  players: Player[]
}

export const momentizeDays = <T>(
  records: Array<Omit<T, 'day'> & { day: string }>
) => records.map((r) => ({ ...r, day: moment(r.day) }))

function formatDashboardData(data: any): DashboardData {
  const momentizedScores = momentizeDays<Score>(data.scores)
  const momentizedTeammateScores = momentizeDays<Score>(data.teammateScores)
  return {
    ...data,
    scores: momentizedScores,
    teammateScores: momentizedTeammateScores,
    notes: momentizeDays(data.notes),
    highFives:
      _(momentizeDays<HighFive>(data.highFives))
      .map((h: any) => ({...h, date: moment(h.date)}))
      .sort((h1, h2) => h1.date.diff(h2.date))
      .value(),
  }
}

export async function getAppData(sessionKey: string): Promise<AppData> {
  const day = moment().format('YYYY-MM-DD')
  const { dashboardData, roundStartDay, ...other } = throwIfNotSuccessful(
    await post(`${apiUrl}/getAppData`, { day, sessionKey })
  )

  return {
    ...other,
    roundStartDay: moment(roundStartDay),
    dashboardData: formatDashboardData(dashboardData),
  }
}

export async function getDashboardData(
  sessionKey: string,
  playerId: string
): Promise<DashboardData> {
  return formatDashboardData(
    await post(`${apiUrl}/getDashboardData`, { sessionKey, playerId })
  )
}

export const getHeadlines = async (sessionKey: string): Promise<Headline[]> =>
  throwIfNotSuccessful(
    (await post(`${apiUrl}/getHeadlines`, { sessionKey }))
    .map((h: Headline) => ({...h, createdAt: moment(h.createdAt)}))
  )

export async function setTargets(
  sessionKey: string,
  targets: Omit<Target, 'id'>[]
) {
  return throwIfNotSuccessful(
    await post(`${apiUrl}/setTargets`, { sessionKey, targets })
  )
}

export interface ScoreUpsert {
  completed: boolean
  day: string
  habitType: NewHabitType
  id?: string
}

export async function toggleScore(sessionKey: string, score: ScoreUpsert) {
  return throwIfNotSuccessful(
    await post(`${apiUrl}/toggleScore`, { sessionKey, score })
  )
}

export interface MiwUpsert {
  id?: string
  day: string
  progress: MiwProgress
}

export async function setMiwProgress(sessionKey: string, score: MiwUpsert) {
  return throwIfNotSuccessful(
    await post(`${apiUrl}/setMiwProgress`, { sessionKey, score })
  )
}

export async function setNote(sessionKey: string, text: string, date: string) {
  return throwIfNotSuccessful(
    await post(`${apiUrl}/setNote`, { sessionKey, text, date })
  )
}

export interface NewHighFive
  extends Omit<HighFive, 'senderId' | 'senderFirstName' | 'id' | 'day' | 'date'>
{
  id?: string
  day: string
}

export async function giveHighFive(sessionKey: string, highFive: NewHighFive) {
  return throwIfNotSuccessful(
    await post(`${apiUrl}/giveHighFive`, { sessionKey, highFive })
  )
}

export async function getProfileData(
  sessionKey: string,
  playerId: string
): Promise<ProfileData> {
  const response = throwIfNotSuccessful(
    await post(`${apiUrl}/getProfileData`, { sessionKey, playerId })
  )
  return {
    ...response,
    scores: momentizeDays(response.scores),
  }
}

export async function changePassword(
  sessionKey: string,
  playerId: string,
  plainTextPassword: string
) {
  return throwIfNotSuccessful(
    await post(`${apiUrl}/changePassword`, {
      sessionKey,
      playerId,
      plainTextPassword,
    })
  )
}

export async function sendInvite({inviteeFirstName, inviteeEmail}: {inviteeFirstName: string, inviteeEmail: string}) {
  const sessionKey = getSessionKey()
  
  const parameters = { sessionKey, inviteeFirstName, inviteeEmail }

  if (process.env.STORYBOOK_IS_RUNNING) {
    console.log(`Sending 'sendInvite' request with these parameters: ${JSON.stringify(parameters)}`)
  } else {
    return throwIfNotSuccessful(
      await post(`${apiUrl}/sendInvite`, parameters)
    )
  }
}

export async function logout(sessionKey: string) {
  try {
    await post(`${apiUrl}/logout`, { sessionKey })
  } catch (e: any) {
    console.error(e.message)
  }
}
